import { ApiError } from '@/api/errors';
import { openapiClient, unwrapResult } from '@/api/openapi-client';
import type {
  Label,
  MarkArrivalBody,
  Reservation,
  ReservationUpdateBody,
  SetArrivalStatusBody,
  StaffReservationCreateBody,
} from '@restaurant/api-client';
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '@/lib/analytics/umami-client';

type ReservationListCache = { data: Reservation[] } & Record<string, unknown>;

/**
 * Write a status/arrival change straight into every cached day list, so the list
 * view re-sections immediately - without waiting for the invalidation refetch to
 * land (or for the list query to be re-activated after navigating back). Only the
 * status fields are patched: the list rows carry table-assignment data the
 * single-reservation wire doesn't, so a wholesale replace would drop it. The
 * follow-up invalidateQueries still reconciles against the server.
 */
function patchReservationInLists(
  queryClient: QueryClient,
  updated: Pick<Reservation, 'id' | 'status' | 'arrivalStatus'>,
) {
  queryClient.setQueriesData<ReservationListCache>(
    { queryKey: ['reservations', 'list'] },
    (old) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.map((r) =>
          r.id === updated.id
            ? { ...r, status: updated.status, arrivalStatus: updated.arrivalStatus }
            : r,
        ),
      };
    },
  );
}

function refetchOnStatusConflict(queryClient: QueryClient, error: unknown) {
  if (error instanceof ApiError && error.code === 'reservation.statusChanged') {
    queryClient.invalidateQueries({ queryKey: ['reservations'] });
  }
}

export function useConfirmReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reservationId }: { reservationId: string }) =>
      unwrapResult<Reservation>(
        await openapiClient.POST('/api/reservations/{reservationId}/confirm', {
          params: { path: { reservationId } },
        }),
      ),
    onError: (error) => refetchOnStatusConflict(queryClient, error),
    onSuccess: (updated) => {
      queryClient.setQueryData(['reservations', updated.id], updated);
      patchReservationInLists(queryClient, updated);
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      trackEvent('reservation_action', { action: 'accept' });
    },
  });
}

/** Reactivate a CANCELLED reservation (→ CONFIRMED). The backend resets
 *  arrivalStatus to WAITING and clears the cancellation reason. */
export function useReactivateReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reservationId }: { reservationId: string }) =>
      unwrapResult<Reservation>(
        await openapiClient.POST('/api/reservations/{reservationId}/reactivate', {
          params: { path: { reservationId } },
        }),
      ),
    onError: (error) => refetchOnStatusConflict(queryClient, error),
    onSuccess: (updated) => {
      queryClient.setQueryData(['reservations', updated.id], updated);
      patchReservationInLists(queryClient, updated);
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}

export function useRejectReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reservationId, reason }: { reservationId: string; reason: string }) =>
      unwrapResult<Reservation>(
        await openapiClient.POST('/api/reservations/{reservationId}/reject', {
          params: { path: { reservationId } },
          body: { reason },
        }),
      ),
    onError: (error) => refetchOnStatusConflict(queryClient, error),
    onSuccess: (updated) => {
      queryClient.setQueryData(['reservations', updated.id], updated);
      patchReservationInLists(queryClient, updated);
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      trackEvent('reservation_action', { action: 'reject' });
    },
  });
}

export function useCancelReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reservationId, reason }: { reservationId: string; reason?: string }) =>
      unwrapResult<Reservation>(
        await openapiClient.POST('/api/reservations/{reservationId}/cancel', {
          params: { path: { reservationId } },
          body: { reason },
        }),
      ),
    onError: (error) => refetchOnStatusConflict(queryClient, error),
    onSuccess: (updated) => {
      queryClient.setQueryData(['reservations', updated.id], updated);
      patchReservationInLists(queryClient, updated);
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}

export function useMarkArrival() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reservationId,
      arrivalStatus,
    }: {
      reservationId: string;
      arrivalStatus: MarkArrivalBody['arrivalStatus'];
    }) =>
      unwrapResult<Reservation>(
        await openapiClient.PATCH('/api/reservations/{reservationId}/arrival', {
          params: { path: { reservationId } },
          body: { arrivalStatus },
        }),
      ),
    onMutate: async ({ reservationId, arrivalStatus }) => {
      await queryClient.cancelQueries({ queryKey: ['reservations', reservationId] });
      const previous = queryClient.getQueryData<Reservation>(['reservations', reservationId]);
      if (previous) {
        queryClient.setQueryData(['reservations', reservationId], { ...previous, arrivalStatus });
        // Re-section the list view at once - the arrival status drives which
        // group a row lands in (arrived / no-show / completed).
        patchReservationInLists(queryClient, { ...previous, arrivalStatus });
      }
      return { previous };
    },
    onError: (_err, { reservationId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['reservations', reservationId], context.previous);
      }
    },
    onSuccess: (_updated, { arrivalStatus }) => {
      trackEvent('reservation_action', { action: arrivalStatus.toLowerCase() });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}

export function useSetReservationArrivalStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reservationId,
      arrivalStatus,
      note,
    }: {
      reservationId: string;
      arrivalStatus: SetArrivalStatusBody['arrivalStatus'];
      note?: string;
    }) =>
      unwrapResult<Reservation>(
        await openapiClient.PUT('/api/reservations/{reservationId}/arrival-status', {
          params: { path: { reservationId } },
          body: { arrivalStatus, note },
        }),
      ),
    onError: (error) => refetchOnStatusConflict(queryClient, error),
    onSuccess: (updated) => {
      queryClient.setQueryData(['reservations', updated.id], updated);
      patchReservationInLists(queryClient, updated);
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}

export function useUpdateReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reservationId,
      data,
    }: {
      reservationId: string;
      data: ReservationUpdateBody;
    }) =>
      unwrapResult<Reservation>(
        await openapiClient.PATCH('/api/reservations/{reservationId}', {
          params: { path: { reservationId } },
          body: data,
        }),
      ),
    onSuccess: (updated) => {
      queryClient.setQueryData(['reservations', updated.id], updated);
      patchReservationInLists(queryClient, updated);
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}

export function useCreateReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      restaurantId,
      data,
    }: {
      restaurantId: string;
      // Single source of truth: the form can pass every accepted field - guest
      // info, source, specialRequest (occasion/seating/dietary/additional notes),
      // internal note - without this partial drifting from the contract.
      data: StaffReservationCreateBody;
    }) =>
      unwrapResult<Reservation>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/reservations/staff', {
          params: { path: { restaurantId } },
          body: data,
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}

export function useSetReservationLabels(reservationId: string) {
  const queryClient = useQueryClient();
  const detailKey = ['reservations', reservationId];

  return useMutation({
    scope: { id: `reservation-labels-${reservationId}` },
    mutationFn: async (labels: Label[]) =>
      unwrapResult<Reservation>(
        await openapiClient.PUT('/api/reservations/{reservationId}/labels', {
          params: { path: { reservationId } },
          body: { labelIds: labels.map((l) => l.id) },
        }),
      ),
    onMutate: async (labels) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<Reservation>(detailKey);
      if (previous) queryClient.setQueryData<Reservation>(detailKey, { ...previous, labels });
      return { previous };
    },
    onError: (_err, _labels, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['reservations', updated.id], updated);
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}
